import { DateTime } from "luxon";
import { DateFormatPreset, dateFormatPresets } from "./presets";

export type { DateFormatPreset };

/**
 * Options type for the date formatter function
 */
export type DateFormatterOptions = {
  /**
   * The locale to use for the date formatter
   * @default "en-US"
   */
  locale?: Intl.LocalesArgument;
  /**
   * The format preset or Intl.DateTimeFormatOptions object
   * - "short": 01/15/2024
   * - "medium": Jan 15, 2024
   * - "long": January 15, 2024
   * - "full": Monday, January 15, 2024
   * - Intl.DateTimeFormatOptions: Custom format (e.g., { weekday: "long", month: "short", day: "numeric" })
   * @default "short"
   */
  format?: DateFormatPreset | Intl.DateTimeFormatOptions;
};

/**
 * Helper function for formatting table cells with plain dates, including i18n support.
 *
 * Accepts plain date strings in the format "YYYY-MM-DD" (like Temporal.PlainDate).
 * For JavaScript Date objects or date-times with timezone information,
 * use `dateTimeFormatter` instead.
 *
 * @param value - The plain date string to format (ISO format "YYYY-MM-DD")
 * @param options - The options for the date formatter
 * @returns The formatted date string or null for undefined/null values
 */
export const dateFormatter = (
  value?: string | null,
  { locale = "en-US", format = "short" }: DateFormatterOptions = {},
) => {
  if (value == null) {
    return null;
  }

  // Parse as plain date (UTC to avoid local timezone shifts)
  const dateTime = DateTime.fromISO(value, { zone: "utc" });

  if (!dateTime.isValid) {
    return null;
  }

  // Convert locale to string for Luxon compatibility
  const localeString = locale?.toString() ?? "en-US";

  // Check if format is a preset string or Intl options object
  if (typeof format === "string") {
    const preset = dateFormatPresets[format];
    return dateTime.setLocale(localeString).toLocaleString(preset);
  }

  // Custom Intl.DateTimeFormatOptions
  return dateTime.setLocale(localeString).toLocaleString(format);
};
